import { axios, returnUndefinedOnStatusCodes } from "utils";

import { ADMIN_PRACTICE_MANAGEMENT_URL as endpointAdmin, PUBLIC_PRACTICE_MANAGEMENT_API_URL as endpointPublic } from "consts";

export const PRACTICE_MANAGEMENT_ENDPOINTS = {
  VERSIONS: "/versions",
  VERSION_NOTES: "/versions/notes",
  CROP_PARAMETERS: "/crops/parameters",
  CROP_EMISSION_FACTORS: "/crops/emission-factors",
  MINERAL_FERTILIZER_EMISSION_FACTORS: "/fertilizers-mineral/emissions-factors",
  ORGANIC_FERTILIZER_EMISSION_FACTORS: "/fertilizers-organic/emissions-factors",
  ORGANIC_FERTILIZER_PARAMETERS: "/fertilizers-organic/parameters",
  SEEDING_EMISSION_FACTORS: "/seedings/emissions-factors",
  PLANT_PROTECTION_EMISSION_FACTORS: "/plant-protections/emissions-factors",
  TILLAGE_EMISSION_FACTORS: "/tillages/emissions-factors",
  UTILITY_EMISSION_FACTORS: "/utility/emissions-factors",
  SOIL_EMISSION_FACTORS: "/soil/emissions-factors",
  PIGLET_PARAMETERS: "/piglets/parameters",
  PORK_PARAMETERS: "/pork/parameters",
};

export type PRACTICE_MANAGEMENT_ENDPOINT = (typeof PRACTICE_MANAGEMENT_ENDPOINTS)[keyof typeof PRACTICE_MANAGEMENT_ENDPOINTS];

// Parameters are unversioned: there is only ever a single per-country draft to read and edit.
const DRAFT_VERSION = "draft";

const readonlyURL = (path: PRACTICE_MANAGEMENT_ENDPOINT, id?: string) =>
  `${endpointPublic ?? "https://dev-practice-management.klim.eco"}${path}${id ? `/${id}` : ""}`;

/**
 * PMS responses sometimes carry a Mongo-internal `id` alongside the domain id
 * (cropId, fertilizerId, ...). It is never part of a request payload, so it is
 * dropped here at the boundary rather than left to be mistaken for the domain
 * id further in.
 */
const withoutInternalId = <T extends object>(record: T): T => {
  const { id: _id, ...rest } = record as T & { id?: unknown };
  return rest as unknown as T;
};

const withoutInternalIds = <T extends object>(records: T[]): T[] => records.map(withoutInternalId);

export type PracticeManagementPublishPayload = {
  email: string;
  userId: string;
  description: string;
  reason: string;
  impactAssessment: string;
  proposedBy: string;
  assessedBy: string;
  decisionMadeBy: string;
  references: string;
};

export type PracticeManagementVersionInfo = {
  description: string;
  reason: string;
  impactAssessment: string;
  proposedBy: string;
  assessedBy: string;
  decisionMadeBy: string;
  references: string;
};

export type PracticeManagementVersionNote = {
  version: string;
  email: string;
  userId: string;
  description: string;
  reason: string;
  impactAssessment: string;
  proposedBy: string;
  assessedBy: string;
  decisionMadeBy: string;
  references: string;
  id: string;
};

export const createPracticeManagementRecord = async <T>(
  practiceManagementEndpoint: PRACTICE_MANAGEMENT_ENDPOINT,
  data: T,
  country: PracticeManagement.CountryCode
): Promise<T> => axios.post(`${endpointAdmin}/records`, { data: { country, endpoint: practiceManagementEndpoint, data } });

export const updatePracticeManagementRecord = async <T>(
  practiceManagementEndpoint: PRACTICE_MANAGEMENT_ENDPOINT,
  id: string | undefined,
  data: T,
  country: PracticeManagement.CountryCode
): Promise<T> => {
  const idSegment = id ? `/${id}` : "";
  return axios.put(`${endpointAdmin}/records${idSegment}`, { data: { country, endpoint: practiceManagementEndpoint, data } });
};

export const deletePracticeManagementRecord = async (
  practiceManagementEndpoint: PRACTICE_MANAGEMENT_ENDPOINT,
  id: string | undefined,
  country: PracticeManagement.CountryCode
): Promise<void> => {
  const idSegment = id ? `/${id}` : "";
  await axios.delete(`${endpointAdmin}/records${idSegment}`, { params: { country, endpoint: practiceManagementEndpoint } });
};

export const publishPracticeManagementDraft = async (payload: PracticeManagementPublishPayload): Promise<void> => {
  await axios.post(`${endpointAdmin}/publish`, payload);
};

export const resetPracticeManagementDraft = async (): Promise<void> => {
  await axios.post(`${endpointAdmin}/draft/reset`, undefined);
};

export const getPracticeManagementVersions = async (): Promise<string[]> => axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.VERSIONS));

export const getPracticeManagementVersionInfo = async (version: string): Promise<PracticeManagementVersionInfo> =>
  axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.VERSIONS, version));

export const getPracticeManagementVersionNotes = async (): Promise<PracticeManagementVersionNote[]> =>
  axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.VERSION_NOTES));

export const getPracticeManagementCropParameter = async (
  cropId: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.CropParameter | undefined> => {
  const parameter = await axios
    .get<PracticeManagement.CropParameter, PracticeManagement.CropParameter>(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.CROP_PARAMETERS, cropId), {
      params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
      // @ts-expect-error custom params can be passed in axios config
      hideErrorMessage: true,
    })
    .catch(returnUndefinedOnStatusCodes([404]));
  return parameter && withoutInternalId(parameter);
};

export const getPracticeManagementCropParameters = async (country: PracticeManagement.CountryCode = "global"): Promise<PracticeManagement.CropParameter[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.CROP_PARAMETERS), {
      params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
    })
  );

export const getPracticeManagementCropEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.CropEmissionFactor[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.CROP_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementMineralFertilizerEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.MineralFertilizerEmissionsFactors[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.MINERAL_FERTILIZER_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementOrganicFertilizerEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.OrganicFertilizerEmissionsFactors[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementOrganicFertilizerParameter = async (
  fertilizerId: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.OrganicFertilizerParameters | undefined> => {
  const parameter = await axios
    .get<PracticeManagement.OrganicFertilizerParameters, PracticeManagement.OrganicFertilizerParameters>(
      readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_PARAMETERS, fertilizerId),
      {
        params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
        // @ts-expect-error custom params can be passed in axios config
        hideErrorMessage: true,
      }
    )
    .catch(returnUndefinedOnStatusCodes([404]));
  return parameter && withoutInternalId(parameter);
};

export const getPracticeManagementOrganicFertilizerParameters = async (
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.OrganicFertilizerParameters[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.ORGANIC_FERTILIZER_PARAMETERS), {
      params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
    })
  );

export const getPracticeManagementSeedingEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.SeedingEmissionFactors[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.SEEDING_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementPlantProtectionEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.PlantProtectionEmissionsFactors[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.PLANT_PROTECTION_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementTillageEmissionFactors = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.TillageEmissionFactors[]> =>
  withoutInternalIds(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.TILLAGE_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementUtilityEmissionsFactor = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.UtilityEmissionsFactor> =>
  withoutInternalId(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.UTILITY_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export const getPracticeManagementSoilEmissionsFactor = async (
  version?: string,
  country: PracticeManagement.CountryCode = "global"
): Promise<PracticeManagement.SoilEmissionsFactor> =>
  withoutInternalId(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.SOIL_EMISSION_FACTORS), {
      params: { country: country.toLocaleLowerCase(), ...(version && { version }) },
    })
  );

export type PracticeManagementDiffCollection = {
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
  changed: Array<{ identity: Record<string, unknown>; before: Record<string, unknown>; after: Record<string, unknown> }>;
};

export type PracticeManagementDiffResponse = {
  from: string;
  to: string;
  collections: Record<string, PracticeManagementDiffCollection>;
};

export const getPracticeManagementDiff = async (from: string, to: string): Promise<PracticeManagementDiffResponse> =>
  axios.get(`${endpointAdmin}/versions/diff`, { params: { from, to } });

export const addNewCountry = async (country: PracticeManagement.CountryCode): Promise<string[]> =>
  axios.post(`${endpointAdmin}/countries`, undefined, { params: { country } });

export const resetCountryToGlobal = async (country: PracticeManagement.CountryCode, endpoint: PRACTICE_MANAGEMENT_ENDPOINT): Promise<void> => {
  await axios.post(`${endpointAdmin}/countries/reset`, undefined, { params: { country, endpoint } });
};

export const getPracticeManagementPigletParameters = async (country: PracticeManagement.CountryCode = "global"): Promise<PracticeManagement.PigletParameters> =>
  withoutInternalId(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.PIGLET_PARAMETERS), {
      params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
    })
  );

export const getPracticeManagementPorkParameters = async (country: PracticeManagement.CountryCode = "global"): Promise<PracticeManagement.PorkParameters> =>
  withoutInternalId(
    await axios.get(readonlyURL(PRACTICE_MANAGEMENT_ENDPOINTS.PORK_PARAMETERS), {
      params: { country: country.toLocaleLowerCase(), version: DRAFT_VERSION },
    })
  );
